import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useApp, useDocumentTitle, useMe } from '../app/context.js';
import { IconShield } from '../components/icons.js';
import { useToast } from '../components/toast.js';
import { otpauthUri } from '../lib/mfa.js';
import { LegalLinks } from './SignInPage.js';

/** Profil: Konto, Betrieb, MFA (Authenticator-App), Abmelden. */
export function ProfilePage() {
  useDocumentTitle('Profil');
  const { session, mfa } = useApp();
  const me = useMe().data!;
  const status = useQuery({ queryKey: ['mfa'], queryFn: () => mfa.status() });

  return (
    <main className="page">
      <header className="page__head page__head--stack">
        <span className="eyebrow">Profil</span>
        <h1 className="page__title">{me.name ?? me.email}</h1>
      </header>

      <section className="card">
        <dl className="kv-list">
          <dt>E-Mail</dt>
          <dd>{me.email}</dd>
          <dt>Betrieb</dt>
          <dd>{me.tenant?.name ?? '–'}</dd>
          <dt>Rollen</dt>
          <dd>
            {[me.tenantAdmin && 'Admin des Betriebs', me.platformAdmin && 'Betriebsam-Administration'].filter(Boolean).join(', ') || 'Mitglied'}
          </dd>
        </dl>
      </section>

      <section className="card">
        <div className="card__head">
          <h2 className="card__title">
            <IconShield size={18} /> Zwei-Faktor-Anmeldung
          </h2>
          {status.data && <span className={`badge ${status.data.enabled ? 'badge--ok' : 'badge--neutral'}`}>{status.data.enabled ? 'aktiv' : 'nicht eingerichtet'}</span>}
        </div>
        {status.data?.enabled ? (
          <p className="note">Bei jeder Anmeldung wird zusätzlich ein Code aus deiner Authenticator-App abgefragt.</p>
        ) : (
          status.data && <MfaSetup email={me.email ?? 'Konto'} />
        )}
        {status.error && <div className="alert alert--error">{(status.error as Error).message}</div>}
      </section>

      <button className="btn btn--outline" onClick={session.logout}>{session.mock ? 'Rolle wechseln (Mock)' : 'Abmelden'}</button>
      <LegalLinks />
    </main>
  );
}

function MfaSetup({ email }: { email: string }) {
  const { mfa, session } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const begin = useMutation({ mutationFn: () => mfa.begin(), onSuccess: setSecret });
  const verify = useMutation({
    mutationFn: () => mfa.verify(code),
    onSuccess: async () => {
      // Neue Tokens holen, damit z. B. die Admin-Gruppe (nur mit MFA) sofort wirkt.
      await session.refresh().catch(() => undefined);
      await Promise.all([qc.invalidateQueries({ queryKey: ['mfa'] }), qc.invalidateQueries({ queryKey: ['me'] })]);
      toast('Zwei-Faktor-Anmeldung ist eingerichtet.');
    },
  });

  useEffect(() => {
    if (secret) void QRCode.toDataURL(otpauthUri(secret, email), { margin: 1, width: 200 }).then(setQr);
  }, [secret, email]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (/^\d{6}$/.test(code.replace(/\s/g, ''))) verify.mutate();
  }

  if (!secret) {
    return (
      <>
        <p className="note">Schützt dein Konto zusätzlich mit einem Code aus einer Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator, 1Password). Für die Betriebsam-Administration Pflicht.</p>
        <button className="btn btn--primary" disabled={begin.isPending} onClick={() => begin.mutate()}>Jetzt einrichten</button>
        {begin.error && <div className="alert alert--error">{(begin.error as Error).message}</div>}
      </>
    );
  }

  return (
    <form onSubmit={submit} className="form">
      <ol className="steps">
        <li>QR-Code mit der Authenticator-App scannen.</li>
        <li>Den angezeigten 6-stelligen Code eingeben.</li>
      </ol>
      {qr && <img src={qr} alt="QR-Code für die Authenticator-App" width={200} height={200} className="qr" />}
      <details className="note">
        <summary>QR-Code lässt sich nicht scannen?</summary>
        <span className="mono" style={{ wordBreak: 'break-all' }}>{secret}</span>
      </details>
      <div className="field">
        <label className="field__label" htmlFor="mfa-code">Code aus der App</label>
        <input id="mfa-code" className="input mono" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} />
      </div>
      {verify.error && <div className="alert alert--error">{(verify.error as Error).message}</div>}
      <button type="submit" className="btn btn--primary" disabled={verify.isPending}>Bestätigen</button>
    </form>
  );
}
